import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Users, Heart, ArrowRight, Share2, QrCode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const EventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Initial event from flyer with updated image
  const initialEvents = [
    {
      id: '9a407f95-8675-4b00-8779-0a9a1d055cd8',
      title: 'EL Bethel | On Campus - WORSHIP Night',
      description: 'Join us for an evening of worship, Christmas carols, music, and stories. Experience the joy of Christmas celebration with our ministry community at Dallas Baptist University.',
      event_date: '2025-12-11T20:00:00Z',
      start_time: '8:00 PM',
      end_time: '10:00 PM',
      venue: 'Rogers Theater, Patty & Bo Pilgrim Chapel',
      address: 'Dallas Baptist University, 3000 Mountain Creek Parkway, Dallas, TX 75211',
      featured_image: 'https://customer-assets.emergentagent.com/job_elbethel-worship/artifacts/54dm58vk_file_00000000fb1c71fdafa2f71e86b37bac.png',
      event_type: 'worship',
      max_capacity: '200',
      registration_required: true,
      status: 'active',
      created_by: 'Sean Talluri',
      special_instructions: 'Appetizers will be served at 7:45 PM. Dinner to follow after the worship service. Please arrive early for fellowship and food. Free parking is available on campus.'
    }
  ];

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      
      // Try to fetch from backend first
      try {
        const response = await fetch(`${API}/events`);
        if (response.ok) {
          const eventsData = await response.json();
          // If API returns empty array, use initial events
          if (eventsData.length === 0) {
            setEvents(initialEvents);
          } else {
            setEvents(eventsData);
          }
        } else {
          throw new Error('API not ready');
        }
      } catch (apiError) {
        console.warn('API not ready, using initial events:', apiError);
        setEvents(initialEvents);
      }
      
    } catch (err) {
      console.error('Error loading events:', err);
      setError('Unable to load events. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      weekday: date.toLocaleDateString('en-US', { weekday: 'long' }),
      fullDate: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    };
  };

  const generateQRCodeUrl = (eventId) => {
    const eventUrl = `${window.location.origin}/events/${eventId}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(eventUrl)}`;
  };

  const shareEvent = (event) => {
    const eventUrl = `${window.location.origin}/events/${event.id}`;
    const shareText = `Join us for ${event.title} on ${formatEventDate(event.event_date).fullDate} at ${event.start_time}`;
    
    if (navigator.share) {
      navigator.share({
        title: event.title,
        text: shareText,
        url: eventUrl,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${shareText} - ${eventUrl}`);
      alert('Event link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="pt-16 lg:pt-20 pb-20 lg:pb-0 min-h-screen">
        <section className="py-12 lg:py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <div className="animate-spin w-12 h-12 border-4 border-[#f17b21] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600 font-sans">Loading upcoming events...</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-16 lg:pt-20 pb-20 lg:pb-0 min-h-screen">
        <section className="py-12 lg:py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <p className="text-red-600 font-sans mb-4">{error}</p>
              <button 
                onClick={fetchEvents}
                className="bg-[#f17b21] hover:bg-[#d86919] text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
              >
                Try Again
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-purple-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              Ministry Events
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto font-sans">
              Join us for worship, fellowship, and community events that strengthen faith 
              and build lasting connections in God's love.
            </p>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          {events.length > 0 ? (
            <div className="space-y-8 lg:space-y-12">
              {events.map((event) => {
                const eventDate = formatEventDate(event.event_date);
                
                return (
                  <div key={event.id} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      {/* Event Image */}
                      <div className="lg:col-span-1">
                        <div className="relative h-64 lg:h-full">
                          <img 
                            src={event.featured_image}
                            alt={event.title}
                            className="w-full h-full object-cover"
                          />
                          {/* Date Badge */}
                          <div className="absolute top-4 left-4 bg-[#f17b21] text-white rounded-lg p-3 text-center">
                            <div className="text-sm font-semibold font-sans">{eventDate.month}</div>
                            <div className="text-2xl font-bold font-display">{eventDate.day}</div>
                          </div>
                        </div>
                      </div>

                      {/* Event Details */}
                      <div className="lg:col-span-2 p-6 lg:p-8">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
                          <div className="flex-1">
                            <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-4 font-display">
                              {event.title}
                            </h2>
                            <p className="text-gray-600 mb-6 leading-relaxed font-sans">
                              {event.description}
                            </p>
                          </div>
                          
                          {/* QR Code */}
                          <div className="lg:ml-8 text-center">
                            <div className="bg-gray-50 p-4 rounded-lg mb-3">
                              <img 
                                src={generateQRCodeUrl(event.id)}
                                alt="Event QR Code"
                                className="w-24 h-24 mx-auto"
                              />
                            </div>
                            <p className="text-xs text-gray-500 font-sans">Scan to RSVP</p>
                          </div>
                        </div>

                        {/* Event Info Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                          <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                              <Calendar className="w-5 h-5 text-[#f17b21]" />
                              <div>
                                <p className="font-semibold text-gray-800 font-sans">{eventDate.fullDate}</p>
                                <p className="text-sm text-gray-600 font-sans">{event.start_time} - {event.end_time}</p>
                              </div>
                            </div>
                            
                            <div className="flex items-start space-x-3">
                              <MapPin className="w-5 h-5 text-[#f17b21] mt-1" />
                              <div>
                                <p className="font-semibold text-gray-800 font-sans">{event.venue}</p>
                                <p className="text-sm text-gray-600 font-sans">{event.address}</p>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                              <Clock className="w-5 h-5 text-[#f17b21]" />
                              <div>
                                <p className="font-semibold text-gray-800 font-sans">Event Type</p>
                                <p className="text-sm text-gray-600 font-sans capitalize">{event.event_type}</p>
                              </div>
                            </div>
                            
                            {event.special_instructions && (
                              <div className="flex items-center space-x-3">
                                <Users className="w-5 h-5 text-[#f17b21]" />
                                <div>
                                  <p className="font-semibold text-gray-800 font-sans">Registration Required</p>
                                  <p className="text-sm text-gray-600 font-sans">RSVP to secure your spot</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Special Instructions */}
                        {event.special_instructions && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                            <h4 className="font-semibold text-gray-800 mb-2 font-sans">Important Information</h4>
                            <p className="text-gray-700 text-sm font-sans leading-relaxed">
                              {event.special_instructions}
                            </p>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4">
                          <button
                            onClick={() => navigate(`/events/${event.id}`)}
                            className="flex-1 bg-[#f17b21] hover:bg-[#d86919] text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans flex items-center justify-center space-x-2"
                          >
                            <Heart className="w-5 h-5" />
                            <span>RSVP Now</span>
                          </button>
                          
                          <button
                            onClick={() => shareEvent(event)}
                            className="border-2 border-[#f17b21] text-[#f17b21] hover:bg-[#f17b21] hover:text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans flex items-center justify-center space-x-2"
                          >
                            <Share2 className="w-5 h-5" />
                            <span>Share Event</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 font-display">No Upcoming Events</h2>
              <p className="text-gray-600 font-sans">Check back soon for upcoming ministry events and gatherings.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsPage;